import { toJS } from 'mobx'
import AnalyticsBase, { SegmentCampaign } from '../../shared-lib/lib/AnalyticsBase'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { getBrowserTimezone } from '../../shared-lib/utils'
import packageJSON from '../../package.json'
import { NavigateFunction } from 'react-router'
import { identifyStatsigUser } from '../lib/statsig'
import { screenSearchData } from 'shared-solo/stores/SearchStore/SearchStore'

// VITE_SEGMENT_WRITE_KEY is "" in DEV which causes an error in the console but it won't send events
const analytics = AnalyticsBrowser.load({ writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY })

// Extend shared AnalyticsBase
class Analytics extends AnalyticsBase {

  private cioEventHandler: ((event: any) => void) | null = null

  get analytics() {
    if (import.meta.env.VITE_SS_ENV === 'development') {
      return undefined
    }

    return analytics
  }

  init() {
    // analytics.debug(true) // Uncomment to see events in the console
    this.config.platform = import.meta.env.VITE_PLATFORM
    this.config.name = import.meta.env.VITE_SS_ENV
    this.config.timezone = getBrowserTimezone()
    this.config.version = packageJSON.version
  }

  page = (pageName: string, data?: any) => {
    const parsedData = this.parseProperties({
      ...toJS(data),
      ...this.getContext(),
    })

    this.analytics?.page(pageName, parsedData)
  }

  extraIdentify = (userId: string, _traits: Record<string, any>) => {
    if (import.meta.env.VITE_SS_ENV === 'development') {
      return
    }

    // We also need to identify with Customer.io to support in app messaging
    window.cioanalytics?.identify({
      id: `PRO_${userId}`,
      iapEnabled: true,
    })

    identifyStatsigUser({
      userID: userId,
      email: _traits.emailAddress,
      custom: {
        ..._traits,
      },
    })
  }

  listenOnCioEvents = (navigate: NavigateFunction, getSearchTerms: () => screenSearchData[]) => {
    if (import.meta.env.VITE_SS_ENV === 'development') {
      return
    }

    this.cleanUpCioEvents()

    this.cioEventHandler = (event: any) => {
      const customerioEvent = event.detail

      switch (customerioEvent.type) {
        case 'in-app:message-opened':
          analytics.track('In-App Message Opened', {
            messageId: customerioEvent.messageId,
          })
          break
        case 'in-app:message-dismissed':
          analytics.track('In-App Message Dismissed', {
            messageId: customerioEvent.messageId,
          })
          break
        case 'in-app:message-action': {
          const [action, data] = customerioEvent?.detail?.actionValue.split(':') || []

          // Always dismiss the message
          customerioEvent.detail.message.dismiss()

          analytics.track('In-App Message Action', {
            messageId: customerioEvent.messageId,
            action,
            data,
          })

          if (action === 'Deeplink') {
            const url = getSearchTerms()?.find((term) =>
              term.primaryTerm === data,
            )?.address.desktopPath

            if (url) {
              navigate(url)
            }
          }

          break
        }
        case 'in-app:message-error':
          // Dismiss the message on error
          customerioEvent.detail.message.dismiss()

          analytics.track('In-App Message Error', {
            messageId: customerioEvent.messageId,
          })

          break
      }
    }

    window.addEventListener('customerio-event', this.cioEventHandler)
  }

  cleanUpCioEvents = () => {
    if (this.cioEventHandler) {
      window.removeEventListener('customerio-event', this.cioEventHandler)
      this.cioEventHandler = null
    }
  }

  getAdAttribution = () => {
    const result: { properties: Record<string, string>, campaign: SegmentCampaign } = {
      properties: {},
      campaign: {},
    }

    if (typeof document === 'undefined') return result
    const cookies: Record<string, string> = {}

    for (const part of document.cookie.split('; ')) {
      if (!part) continue
      const i = part.indexOf('=')
      if (i === -1) continue
      cookies[part.slice(0, i)] = (() => { try { return decodeURIComponent(part.slice(i + 1)) } catch { return part.slice(i + 1) } })()
    }

    if (cookies._fbc) result.properties.fbc = cookies._fbc
    if (cookies._fbp) result.properties.fbp = cookies._fbp

    const utmToCampaign: Record<string, keyof SegmentCampaign> = {
      utm_source: 'source',
      utm_medium: 'medium',
      utm_campaign: 'name',
      utm_term: 'term',
      utm_content: 'content',
    }

    if (cookies.ss_attribution) {
      try {
        const parsed = JSON.parse(cookies.ss_attribution)
        for (const [k, v] of Object.entries(parsed)) {
          const campaignKey = utmToCampaign[k]
          if (campaignKey) result.campaign[campaignKey] = v as string
          else result.properties[k] = v as string
        }
      } catch {
        // ignore malformed attribution cookie
      }
    }

    return result
  }
}

export default new Analytics()
